// Story 1.20 — TanStack mutation for POST /api/v1/account/data-export.
//
// Each submit generates a fresh `crypto.randomUUID()` idempotency key
// — same pattern as Stories 1.16/1.17/1.18/1.19.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RequestExportResponse = {
  export_request_id: string;
  status: "pending";
  estimated_ready_in_minutes: number;
};

export type RequestExportError = {
  code: string;
  message: string;
};

async function postDataExportRequest(
  idempotencyKey: string,
): Promise<RequestExportResponse> {
  const response = await fetch("/api/v1/account/data-export", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: "{}",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RequestExportError = {
      code: "DATA_EXPORT_REQUEST_FAILED",
      message: `Data-export request failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as { data: RequestExportResponse };
  return envelope.data;
}

export function useRequestDataExport() {
  const queryClient = useQueryClient();
  return useMutation<RequestExportResponse, RequestExportError, void>({
    mutationFn: () => postDataExportRequest(crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dsar-status"] });
    },
  });
}
